import { useMutation } from '@tanstack/react-query';
import { postDataProcessor } from '@leagueplatform/masonry-api';
import { usePatientPortalContext } from 'contexts/patient-portal-provider.context';
import { PATIENT_PORTAL_SYSTEM_UUIDS } from 'types/patient-portal-system.types';
import {
  MESSAGE_FIELD_ID,
  SUCCESS_FIELD_ID,
  type MobileVerificationOtpResult,
} from 'types/data-processor.types';
import { getFieldValue } from 'utils/data-processor.utils';

type VerifyOtpRequest = {
  mobileNumber: string;
  otp: string;
};

const VERIFY_OTP_DATA_PROCESSOR_ID = 'verifyOtp';
const CONTACT_FIELD_ID = 'contact';
const CHANNEL_FIELD_ID = 'channel';
const OTP_FIELD_ID = 'otp';
const SYSTEM_ID_FIELD_ID = 'systemId';
const DEFAULT_CHANNEL_TYPE = 'sms';

export const useVerifyOtp = () => {
  const { systemId } = usePatientPortalContext();
  const systemUuid = PATIENT_PORTAL_SYSTEM_UUIDS[systemId];

  return useMutation<MobileVerificationOtpResult, Error, VerifyOtpRequest>({
    mutationFn: async ({ mobileNumber, otp }) => {
      const response = await postDataProcessor({
        dataProcessorId: VERIFY_OTP_DATA_PROCESSOR_ID,
        fields: [
          { id: CONTACT_FIELD_ID, value: mobileNumber },
          { id: CHANNEL_FIELD_ID, value: DEFAULT_CHANNEL_TYPE },
          { id: OTP_FIELD_ID, value: otp },
          { id: SYSTEM_ID_FIELD_ID, value: systemUuid },
        ],
      });

      if (response.status !== 'success') {
        throw new Error(
          response.message ||
            response.errors?.[0]?.message ||
            'Failed to verify OTP',
        );
      }

      const success = getFieldValue(response.fields, SUCCESS_FIELD_ID) === true;

      if (!success) {
        const rawMessage = getFieldValue(response.fields, MESSAGE_FIELD_ID);
        const message =
          typeof rawMessage === 'string' && rawMessage ? rawMessage : undefined;
        return { success: false, message };
      }

      return { success: true };
    },
  });
};
